import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core'
import { firstValueFrom, Observable, Subscription } from 'rxjs'
import { CodeLookup, PaginatedAPI, QueriesAPIModel, UserAPIModel } from 'src/app/models/api.model'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { getQuerySummaryList } from 'src/app/store/selector/query.selector'
import { load_query_summary_action } from 'src/app/store/action/query.action'
import {
  QUERIES_ID,
  QUERY_DAYS_ELAPSED,
  QUERY_REQUEST_INITIATED,
  QUERY_RESPONSE_DUE,
  OWNER_AUTOCOMPLETE_DISPLAY_ID,
  LAST_UPDATED_BY,
  LAST_UPDATED_ON,
  NEXT_INTERNAL_DUE_DATE,
  QUERY_CATEGORY_SELECT,
  PRIORITY_LEVEL_SELECT,
  NEXT_ACTION_SELECT,
  QUERY_TAG_MULTISELECT,
  QUERY_TASK_CURRENT_STATUS_MULTISELECT,
  ASSIGNED_TO_MULTISELECT,
  TASK_SUB_CATEGORY,
  COMPANY_ENTITY_DISPLAY
} from 'src/app/constants/ui.columns'
import { ServiceUrlConstants } from '../../../constants/service-url-constants'
import { DocumentService } from '../../../services/common/document.service'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  getSelectedCompaniesFromState,
  getStatusMapFromTaskTypes,
  getTaskCategorySubCategoryMapFromState,
  getUIStatusMappingFromState,
  getUniqueNextActionFromTaskTypes
} from '../../../store/selector/initialize.selector'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { QUERY } from 'src/app/constants/field-id-constants'
import { OptionsService } from '../../../services/common/options.service'
import { CommonService, generateOptionsMap, generateOptionsMapFromSet } from '../../../services/common/common.service'
import { UserService } from '../../../services/user/user.service'
import { ActivatedRoute, Router } from '@angular/router'
import { SummaryTableService } from 'src/app/services/common/summary-table.service'
import { da } from 'date-fns/locale'

@Component({
  selector: 'app-query-summary',
  templateUrl: './query-summary.component.html',
  styleUrls: ['./query-summary.component.scss']
})
export class QuerySummaryComponent implements OnDestroy {
  fromDate: any
  toDate: any
  querySummaryList$: Observable<PaginatedAPI<QueriesAPIModel>>
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<QueriesAPIModel>
  private nextActionMap: Map<string, string>
  private statusMap: Map<string, string>
  private assigneeMap: Map<string, string>
  selectedViewForOption: any
  viewForOptions: any[]
  private taskSubCategoryMap: Map<string, string>
  companyEntityMap: Map<string, string>
  getSelectedCompaniesFromState$: Subscription

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'createdDatetime',
    sortOrder: 'DESC'
  }
  searchRequestBody: any = {
    createdDatetimeRange: undefined
  }
  pageTitle = 'Query Stats Summary'
  dashboardPageLink = `/${GlobalConstants.ROUTING.QUERY}/${GlobalConstants.ROUTING.DASHBOARD}`
  queryCategoryMap: Map<string, string> = new Map<string, string>()
  queryTagOptions: CodeLookup[]
  priorityLevelMap: Map<string, string> = new Map<string, string>()
  readonly MODULE_NAME_QUERIES = GlobalConstants.MODULE.QUERIES
  readonly CATEGORY_TYPE_ESI = GlobalConstants.CATEGORY_TYPE.EPF
  assigneesSubscription$: Subscription
  isVisible: boolean = true
  private queryParamsProcessed: boolean = false

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    private optionsService: OptionsService,
    private userService: UserService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private readonly summaryTableService: SummaryTableService,
    private readonly cs: CommonService
  ) {
    this.querySummaryList$ = this.appState.select(getQuerySummaryList())
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: true,
      contentSelectable: true,
      contentSelector: true,
      filterable: true,
      formattedContent: false,
      pageable: true,
      sortable: true,
      quickFilters: true
    }
  }

  ngOnInit(): void {
    this.populateOptions()
    this.callGetAssigneeListAPI()
    this.subscribeToAppState()
    this.initializeViewForOptions()
    this.initializeSummaryData()
    this.addFiltersWithDynamicOptions()
    this.initializeQueryParams()
  }

  private initializeQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (this.queryParamsProcessed) {
        return
      }
      const status = params['status']
      if (status) {
        this.updateStatusLists(status)
        this.summaryData = {
          ...this.summaryData,
          appliedFilters: { ...this.summaryData.appliedFilters, statusList: this.searchRequestBody.statusList }
        }
        this.loadSummaryData(false)
        this.queryParamsProcessed = true
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        })
      } else {
        this.loadStateData()
      }
    })
  }

  private updateStatusLists(status: string): void {
    const statusList = []

    switch (status) {
      case 'QR':
        statusList.push('QUERY_RECEIVED')
        break
      case 'INP':
        statusList.push(
          'AWAITING_CLOSURE_CONFIRMATION_FROM_EE',
          'MOVED_TO_OFFLINE_SUBMISSION',
          'PENDING_WITH_EE',
          'IN_PROGRESS',
          'PENDING_WITH_EPFO',
          'PENDING_WITH_EMPLOYER',
          'MOVED_TO_FRESHDESK_TERMINATED_EE',
          'ACTIVITY_RECEIVED'
        )
        break
      case 'COMPLETED':
        statusList.push(status)
        break
    }

    this.searchRequestBody = {
      ...this.searchRequestBody,
      statusList
    }
  }

  private subscribeToAppState(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe(uiStatusMapping => {
      if (uiStatusMapping) {
      }
    })

    this.appState.select(getUniqueNextActionFromTaskTypes(this.MODULE_NAME_QUERIES)).subscribe(uniqueNextActions => {
      if (uniqueNextActions) {
        this.nextActionMap = uniqueNextActions
      }
    })

    this.appState.select(getStatusMapFromTaskTypes(this.MODULE_NAME_QUERIES)).subscribe(statusMap => {
      if (statusMap) {
        this.statusMap = statusMap
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState(this.MODULE_NAME_QUERIES))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          const subCategoriesArr = Array.from(categorySubCategoryMap?.values()).flatMap(array => array)
          const uniqueSubCategories = new Set(subCategoriesArr)
          this.taskSubCategoryMap = generateOptionsMapFromSet(uniqueSubCategories)
        }
      })
    this.getSelectedCompaniesFromState$ = this.appState
      .select(getSelectedCompaniesFromState)
      .subscribe(selectedCompanies => {
        if (selectedCompanies) {
          const filteredCompanies = selectedCompanies.filter(sc => sc.type === GlobalConstants.COMPANY_TYPE.ENTITY)
          this.companyEntityMap = new Map<string, string>()
          filteredCompanies?.forEach(company => {
            this.companyEntityMap.set(company.id, company.displayCode)
          })
        }
      })
  }

  private initializeViewForOptions(): void {
    this.viewForOptions = this.summaryTableService.buildViewForOption()
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = {
      ...this.searchRequestBody,
      ...this.buildCreatedatetimeFilter(firstViewForOption)
    }
  }

  private initializeSummaryData(): void {
    this.summaryData = {
      content$: this.querySummaryList$,
      contentSelector: {
        label: 'View for',
        selectedValue: this.selectedViewForOption,
        selectionOptions: this.viewForOptions,
        onChange: this.onViewForChange
      },
      formattingLegend: [
        { formatLabel: 'High Priority', formatClassName: 'priority-high' },
        { formatLabel: 'Medium Priority', formatClassName: 'priority-medium' },
        { formatLabel: 'Low Priority', formatClassName: 'priority-low' }
      ],
      headers: [
        QUERIES_ID,
        OWNER_AUTOCOMPLETE_DISPLAY_ID,
        QUERY_REQUEST_INITIATED,
        QUERY_DAYS_ELAPSED,
        QUERY_RESPONSE_DUE,
        LAST_UPDATED_BY,
        LAST_UPDATED_ON,
        NEXT_INTERNAL_DUE_DATE
      ],
      additionalButtons: [
        {
          id: 'download-query-details-pdf-files',
          label: 'Query PDF',
          callback: this.downloadQueryDetailsPDFZipCallback,
          actionType: 'download'
        }
      ],
      currentSearchParams: this.queryParamValues,
      sortCallback: this.sortCallback,
      checkBoxSelectionIds: [],
      searchCallback: this.searchCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange,
      downloadDocumentCallback: this.downloadDocumentCallback,
      quickFilters: [
        {
          id: 'inActiveQueries',
          label: 'Inactive Queries',
          criteria: [
            { formControlName: 'employeeStatus', filterValue: 'TERMINATED' },
            { formControlName: 'notInStatusList', filterValue: ['COMPLETED'] }
          ]
        }
      ]
    }
  }

  private loadStateData(): void {
    this.appState.select('common').subscribe(data => {
      const searchParams = data?.summaryTableData?.get(QUERY)
      if (searchParams) {
        this.applySearchParams(searchParams)
      } else {
        this.loadSummaryData(false)
      }
    })
  }

  private applySearchParams(searchParams: any): void {
    this.searchRequestBody = {
      ...this.searchRequestBody,
      createdDatetimeRange: searchParams.searchRequestBody?.createdDatetimeRange
    }

    const contentSelector = {
      ...this.summaryData.contentSelector,
      selectedValue: searchParams.selectedViewForOption
    }

    this.summaryData = {
      ...this.summaryData,
      appliedFilters: searchParams.searchRequestBody,
      contentSelector
    }

    this.appState.dispatch(load_query_summary_action(searchParams))
  }

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.selectedViewForOption = $event.target.value
      const searchRequestBody = { ...this.searchRequestBody, ...this.buildCreatedatetimeFilter($event.target.value) }
      this.searchRequestBody = searchRequestBody
      this.queryParamValues.sortBy = 'createdDatetime'
      this.queryParamValues.sortOrder = 'DESC'
    } else {
      const searchRequestBody = { ...this.searchRequestBody, createdDatetimeRange: undefined }
      this.searchRequestBody = searchRequestBody
      this.selectedViewForOption = null
    }
    this.loadSummaryData()
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  back() {}

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  private loadSummaryData(saveSearchParams: boolean = true): void {
    const requestBody = { ...this.searchRequestBody }

    const includeStatus = requestBody['statusList.isInclude']
    const statusList = requestBody['statusList']

    if (includeStatus && Array.isArray(statusList) && statusList.length > 0) {
      // If includeStatus is false, the server expects `notInStatusList`
      if (includeStatus === 'false') {
        requestBody['notInStatusList'] = statusList
        delete requestBody['statusList']
      }
      delete requestBody['statusList.isInclude']
    }

    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: requestBody
    }

    this.appState.dispatch(load_query_summary_action(options))
    if (saveSearchParams) {
      this.appState.dispatch(
        SAVE_SUMMARY_TABLE_SEARCH_PARAMS({
          owner: QUERY,
          data: { ...options, selectedViewForOption: this.selectedViewForOption }
        })
      )
    }
  }

  //TODO: refactor code specially for reset
  searchCallback = (searchParams, button, reset: boolean) => {
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }

    const buttonFilters = {}
    if (button?.id === 'inActiveQueries') {
      searchParams = {}
      this.isVisible = false
      this.summaryData.appliedFilters = {}
      this.cdr.detectChanges()
      this.isVisible = true
      button?.criteria.forEach(({ formControlName, filterValue }) => {
        if (formControlName) {
          buttonFilters[formControlName] = filterValue
        }
      })
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      ...this.buildCreatedatetimeFilter(this.selectedViewForOption),
      ...buttonFilters
    }
    if (reset) {
      this.resetFilters()
    }
    this.loadSummaryData()
  }

  rowFormattingForDaysElapsed = (record: QueriesAPIModel): string => {
    if (record.daysElapsed > 7) {
      return 'priority-high'
    } else if (record.daysElapsed > 0 && record.daysElapsed <= 7) {
      return 'priority-medium'
    } else {
      return 'priority-low'
    }
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds?.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_QUERY_SUMMARY_LIST + ServiceUrlConstants.API_EXPORT + documentType}`

    const requestBody = { ...this.searchRequestBody }

    const includeStatus = requestBody['statusList.isInclude']
    const statusList = requestBody['statusList']

    if (includeStatus && Array.isArray(statusList) && statusList.length > 0) {
      // If includeStatus is false, the server expects `notInStatusList`
      if (includeStatus === 'false') {
        requestBody['notInStatusList'] = statusList
        delete requestBody['statusList']
      }
      delete requestBody['statusList.isInclude']
    }
    if (!requestBody?.createdDatetimeRange) {
      delete requestBody['createdDatetimeRange']
    }
    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: requestBody
    }

    this.documentService.downloadDocument(endPoint, options)
  }

  downloadQueryDetailsPDFZipCallback = async () => {
    const data = await firstValueFrom(this.querySummaryList$)

    if (data.totalElements === 0) {
      this.cs.showError('No queries to download')
      return
    }

    if (data.totalElements > 80) {
      this.cs.showError('Cannot be downloaded for more than 80 queries')
      return
    }

    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds?.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }

    const requestBody = { ...this.searchRequestBody }

    const includeStatus = requestBody['statusList.isInclude']
    const statusList = requestBody['statusList']

    if (includeStatus && Array.isArray(statusList) && statusList.length > 0) {
      // If includeStatus is false, the server expects `notInStatusList`
      if (includeStatus === 'false') {
        requestBody['notInStatusList'] = statusList
        delete requestBody['statusList']
      }
      delete requestBody['statusList.isInclude']
    }
    if (!requestBody?.createdDatetimeRange) {
      delete requestBody['createdDatetimeRange']
    }
    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: requestBody
    }

    this.documentService.downloadDocument(ServiceUrlConstants.API_QUERIES_DETAILS_ZIP, options)
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  dateSelection() {
    if (this.fromDate && this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: this.fromDate,
        toDate: this.toDate
      }
      this.loadSummaryData()
    }
    if (!this.fromDate && !this.toDate) {
      this.searchRequestBody = {
        ...this.searchRequestBody,
        fromDate: null,
        toDate: null
      }
      this.loadSummaryData()
    }
  }

  ngOnDestroy(): void {
    this?.assigneesSubscription$?.unsubscribe()
  }

  populateOptions() {
    let lookupValues: string[] = [
      GlobalConstants.OPTION_TYPE.QUERY_TAG,
      GlobalConstants.OPTION_TYPE.QUERY_CATEGORY,
      GlobalConstants.OPTION_TYPE.PRIORITY_LEVEL
    ]
    this.optionsService.getOptionsForType(lookupValues).subscribe({
      next: responses => {
        if (responses) {
          this.queryCategoryMap = generateOptionsMap(responses[GlobalConstants.OPTION_TYPE.QUERY_CATEGORY])
          this.queryTagOptions = responses[GlobalConstants.OPTION_TYPE.QUERY_TAG]
          this.priorityLevelMap = generateOptionsMap(responses[GlobalConstants.OPTION_TYPE.PRIORITY_LEVEL])
        }
      }
    })
  }

  callGetAssigneeListAPI() {
    this.assigneesSubscription$ = this.userService.invokeFetchAssigneeList().subscribe((assignees: UserAPIModel[]) => {
      if (assignees) {
        const sortedAssignees = assignees.sort(
          (a, b) => new Date(b.passwordExpirationDate).getTime() - new Date(a.passwordExpirationDate).getTime()
        ) //sorting using passExpDate in desc for dropdown
        const assigneeMap = new Map<string, string>()
        sortedAssignees.forEach(assignee => {
          assigneeMap.set(assignee.id, assignee.fullName)
        })
        this.assigneeMap = assigneeMap
      }
    })
  }

  addFiltersWithDynamicOptions() {
    COMPANY_ENTITY_DISPLAY.filter.getOptionsList = () => {
      return this.companyEntityMap
    }
    COMPANY_ENTITY_DISPLAY.build = (data: any) => {
      return this.companyEntityMap?.get(data?.[COMPANY_ENTITY_DISPLAY.key])
    }
    this.summaryData?.headers?.splice(0, 0, COMPANY_ENTITY_DISPLAY)

    //Category of Query
    QUERY_CATEGORY_SELECT.filter.getOptionsList = () => {
      return this.queryCategoryMap
    }
    QUERY_CATEGORY_SELECT.build = (data: any) => {
      return this.queryCategoryMap?.get(data?.[QUERY_CATEGORY_SELECT.key])
    }

    //Priority Level
    PRIORITY_LEVEL_SELECT.filter.getOptionsList = () => {
      return this.priorityLevelMap
    }
    PRIORITY_LEVEL_SELECT.build = (data: any) => {
      return this.priorityLevelMap?.get(data?.[PRIORITY_LEVEL_SELECT.key])
    }

    //Next Action to be taken
    NEXT_ACTION_SELECT.filter.getOptionsList = () => {
      return this.nextActionMap
    }

    //Tags
    QUERY_TAG_MULTISELECT.filter.getOptionsList = () => {
      return this.queryTagOptions?.reduce((map, queryTag) => {
        map.set(queryTag.key2, queryTag.key2)
        return map
      }, new Map<string, string>())
    }

    // Task Category
    TASK_SUB_CATEGORY.filter.getOptionsList = () => {
      return this.taskSubCategoryMap
    }
    this.summaryData?.headers?.splice(1, 0, TASK_SUB_CATEGORY)

    const responseDueIndex = this.summaryData?.headers?.findIndex(header => header.key === QUERY_RESPONSE_DUE.key)
    if (responseDueIndex !== -1) {
      QUERY_TASK_CURRENT_STATUS_MULTISELECT.filter.getOptionsList = () => {
        return this.statusMap
      }

      ASSIGNED_TO_MULTISELECT.filter.getOptionsList = () => {
        return this.assigneeMap
      }
      ASSIGNED_TO_MULTISELECT.build = (data: any) => {
        return this.assigneeMap?.get(data?.[ASSIGNED_TO_MULTISELECT.key]) || data?.['assignedToDisplayId'] || ''
      }

      // Current Status
      this.summaryData?.headers?.splice(responseDueIndex + 1, 0, QUERY_TASK_CURRENT_STATUS_MULTISELECT)
      // Assigned TO
      this.summaryData?.headers?.splice(responseDueIndex + 2, 0, ASSIGNED_TO_MULTISELECT)
    }

    this.summaryData?.headers?.push(
      QUERY_CATEGORY_SELECT,
      PRIORITY_LEVEL_SELECT,
      NEXT_ACTION_SELECT,
      QUERY_TAG_MULTISELECT
    )
  }

  buildCreatedatetimeFilter(value: string) {
    if (!value) {
      return null
    }
    const daterange = value.split('^')
    return { createdDatetimeRange: { begin: new Date(daterange[0]), end: new Date(daterange[1]) } }
  }

  resetFilters() {
    this.isVisible = false
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = {
      ...this.summaryTableService.buildEmptyCreatedatetimeFilterIfValueIsNull(firstViewForOption)
    }
    this.queryParamValues = {
      pageNo: 0,
      pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
      sortBy: 'createdDatetime',
      sortOrder: 'DESC'
    }
    this.fromDate = null
    this.toDate = null
    this.initializeSummaryData()
    this.addFiltersWithDynamicOptions()
    this.cdr.detectChanges()
    this.isVisible = true
  }
}
